/**
 * Expertise Discovery (section 22f) — pure, client-side "who knows about X?".
 * Given a topic, it finds the entities that match (workstreams, completed &
 * in-flight stories, authored/owned/reviewed documents, decisions, owned
 * services) and folds the people attached to those entities into a list of
 * STARTING POINTS, each annotated with WHY they surfaced (their roles) and the
 * supporting entities.
 *
 * STRICT: this surfaces awareness + connections only. It assigns NO score to
 * people, produces NO ranking/leaderboard, and counts nothing per person. The
 * result is ordered alphabetically (individuals before teams) precisely so it
 * cannot be read as a ranking of who is "best".
 *
 * Pure TS — no React.
 */

import { scoreText } from './intelligenceModel';
import type { EntityRef, EntitySnapshot, ExpertHit, ExpertiseResult } from './types';

/** Accumulates a role + supporting ref onto a person/team, keyed case-insensitively. */
class ExpertAccumulator {
  private readonly map = new Map<string, ExpertHit>();

  add(who: string | undefined, role: string, ref: EntityRef, isTeam = false): void {
    const key = (who ?? '').trim();
    if (!key) return;
    const mapKey = `${isTeam ? 'team:' : 'user:'}${key.toLowerCase()}`;
    const existing = this.map.get(mapKey);
    if (existing) {
      if (!existing.roles.includes(role)) existing.roles.push(role);
      if (!existing.refs.some((r) => r.id === ref.id)) existing.refs.push(ref);
      return;
    }
    this.map.set(mapKey, { who: key, roles: [role], refs: [ref], isTeam });
  }

  /** Individuals before teams, then alphabetical — deliberately NOT ranked. */
  values(): ExpertHit[] {
    return Array.from(this.map.values()).sort((a, b) => {
      if (!!a.isTeam !== !!b.isTeam) return a.isTeam ? 1 : -1;
      return a.who.localeCompare(b.who);
    });
  }
}

export interface DiscoverExpertsOptions {
  /** Max supporting entities to consider per kind (keeps derivation cheap). */
  perKindLimit?: number;
}

/**
 * Discovers experts for a topic over the snapshot. `refs` (from
 * {@link ./contextRetriever.toEntityRefs}) supply the display/link projection
 * for supporting entities. Returns `{ experts, matchedRefs }`; an empty topic
 * yields an empty result.
 */
export function discoverExperts(
  snapshot: EntitySnapshot,
  refs: EntityRef[],
  topic: string,
  options: DiscoverExpertsOptions = {},
): ExpertiseResult {
  const t = topic.trim();
  if (!t) return { topic: t, experts: [], matchedRefs: [] };
  const perKindLimit = options.perKindLimit ?? 60;

  const refById = new Map<string, EntityRef>();
  for (const r of refs) refById.set(r.id, r);

  const matched: EntityRef[] = [];
  const acc = new ExpertAccumulator();

  const scored = <T>(items: T[], text: (item: T) => string) =>
    items
      .map((item) => ({ item, s: scoreText(text(item), t) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, perKindLimit)
      .map((x) => x.item);

  // Workstreams — the engineer owning it and its POC.
  for (const w of scored(snapshot.workstreams, (w) =>
    [w.title, w.currentStage, ...(w.systemsAffected ?? [])].join(' '),
  )) {
    const ref = refById.get(w.id);
    if (!ref) continue;
    matched.push(ref);
    acc.add(w.engineer, 'Workstream owner', ref);
    if (w.poc && w.poc.toLowerCase() !== (w.engineer ?? '').toLowerCase()) {
      acc.add(w.poc, 'Workstream POC', ref);
    }
  }

  // Stories (tasks) — the assignee; completed stories get a distinct role.
  for (const s of scored(snapshot.tasks, (s) =>
    [s.title, s.description ?? '', ...(s.tags ?? [])].join(' '),
  )) {
    const ref = refById.get(s.id);
    if (!ref) continue;
    matched.push(ref);
    const role = s.status === 'DONE' ? 'Completed story' : 'Story assignee';
    acc.add(s.assignee, role, ref);
  }

  // Documents — owner, authors, reviewers.
  for (const d of scored(snapshot.documents, (d) =>
    [d.title, d.summary, d.contextWhy, ...(d.tags ?? []), ...(d.relatedServices ?? [])].join(
      ' ',
    ),
  )) {
    const ref = refById.get(d.id);
    if (!ref) continue;
    matched.push(ref);
    acc.add(d.ownerPoc, 'Doc owner', ref);
    for (const a of d.authors ?? []) acc.add(a, 'Doc author', ref);
    for (const r of d.reviewers ?? []) acc.add(r, 'Doc reviewer', ref);
  }

  // Decisions — owner + participants.
  for (const dec of scored(snapshot.decisions, (dec) =>
    [dec.decision, dec.context, dec.decisionMade, dec.why, ...(dec.affectedServices ?? [])].join(
      ' ',
    ),
  )) {
    const ref = refById.get(dec.id);
    if (!ref) continue;
    matched.push(ref);
    acc.add(dec.owner, 'Decision owner', ref);
    for (const p of dec.participants ?? []) acc.add(p, 'Decision participant', ref);
  }

  // Services — primary/secondary POC + owning team.
  for (const svc of scored(snapshot.services, (svc) =>
    [svc.name, svc.purpose, ...(svc.repositories ?? []), ...(svc.knownDependencies ?? [])].join(
      ' ',
    ),
  )) {
    const ref = refById.get(svc.id);
    if (!ref) continue;
    matched.push(ref);
    acc.add(svc.primaryPoc, 'Service primary POC', ref);
    acc.add(svc.secondaryPoc, 'Service secondary POC', ref);
    acc.add(svc.owningTeam, 'Owning team', ref, true);
  }

  return { topic: t, experts: acc.values(), matchedRefs: matched };
}
